import React, {
  ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { Card, DatePicker, Flex, Typography } from 'antd'
import {
  CButton,
  CFormInput,
  CMultiSelect,
  CSmartTable,
  CSpinner,
} from '@coreui/react-pro'
import {
  $hasMoreTransactionsDetail,
  $loadedPageOfTransactionsDetail,
  $optionsWallet,
  $transactionsDetails,
  getTransactionsDetails,
  loadWalletsWithArchive,
  transactionsDetailsMounted,
} from './model'
import { useStore, useUnit } from 'effector-react'
import { useInfiniteScroll } from '../../../../../libs'
import { Option } from '@coreui/react-pro/src/components/multi-select/types'
import {
  TRANSACTIONS_PAGE_SIZE,
  transactionDetailsType,
  transactionsRangePresets,
  transactionTableColumns,
} from './const'
import {
  RangeValue,
  TransactionModalState,
  TransactionsDetailsItemFromServer,
} from './types'
import { SorterValue } from '@coreui/react-pro/src/components/smart-table/types'
import { getTransactionItems } from './utils'
import { Currency } from '../../../../../views/widgets/walletCard/types'
import { GeneralTransactionModal } from '../../../wallets/atoms/GeneralTransactionModal'
import {
  $currencies,
  allCurrenciesMounted,
} from '../../../wallets/details/model'
import { mapScopedColumns } from './mapScopedColumns'
import { OtherTransactionModal } from '../../../wallets/atoms/OtherTransactionModal'
import { walletAllowedListMounted } from '../../../wallets/atoms/GeneralTransactionModal/model'
import { TransferTransactionModal } from '../../../wallets/atoms/TransferTransactionModal'
import { DividendTransactionModal } from '../../../wallets/atoms/DividendTransactionModal'
import { InvestmentTransactionModal } from '../../../wallets/atoms/InvestmentTransactionModal'
import { BudgetPaymentTransactionModal } from '../../../wallets/atoms/BudgetPaymentTransactionModal'
import CIcon from '@coreui/icons-react'
import { cilDataTransferDown } from '@coreui/icons'
import { FilterModal } from '../FilterModal'
import { CommissionPaymentTransactionModal } from '../../../wallets/atoms/CommissionPaymentTransactionModal'

type Props = {
  allCurrencies: Currency[]
}

export const TransactionsDetails: React.FC<Props> = ({ allCurrencies }) => {
  const [columnSorter, setColumnSorter] = useState<SorterValue>({
    column: '',
    state: '',
  })
  const currencies = useStore($currencies)

  const [isModalOpen, setIsModalOpen] = useState(false)

  const [isOpenEditModal, setIsOpenEditModal] = useState<TransactionModalState>(
    {
      createBudgetPaymentModal: false,
      createCommissionPaymentModal: false,
      createOtherModal: false,
      createTransferModal: false,
      createInvestmentModal: false,
      createDividendModal: false,
      transaction: null,
    },
  )
  const [isFilterModalVisible, setIsFilterModalVisible] = useState(false)

  const openFilterModal = () => {
    setIsFilterModalVisible(true)
  }

  const closeFilterModal = () => {
    setIsFilterModalVisible(false)
  }
  // Номер запрошенной страницы нужен только чтобы отличить первую загрузку
  // от подгрузки; какую страницу грузить дальше, решает модель.
  const [requestedPage, setRequestedPage] = useState(1)
  const [reloadNonce, setReloadNonce] = useState(0)
  const [searchText, setSearchText] = useState('')
  const [filtersState, setFiltersState] = useState<{
    [key: string]: string | string[] | RangeValue
  }>({})

  const optionsWallet = useUnit($optionsWallet)

  const transactionsDetails = useStore($transactionsDetails)
  const transactionsLoading = useStore(getTransactionsDetails.pending)
  const loadedPage = useStore($loadedPageOfTransactionsDetail)
  const hasMore = useStore($hasMoreTransactionsDetail)

  const buildRequest = useCallback(
    (page: number) => ({
      limit: TRANSACTIONS_PAGE_SIZE,
      page,
      orderBy: columnSorter.column as string,
      orderDirection: columnSorter.state as string,
      searchText,
      filters: filtersState,
    }),
    [columnSorter.column, columnSorter.state, searchText, filtersState],
  )

  const requestPage = useCallback(
    (page: number) => {
      setRequestedPage(page)
      transactionsDetailsMounted(buildRequest(page))
    },
    [buildRequest],
  )

  // Смена фильтров, поиска, сортировки и явная перезагрузка — всегда с первой
  // страницы, модель на первой странице заменяет список целиком.
  useEffect(() => {
    requestPage(1)
  }, [requestPage, reloadNonce])

  const reloadTransactions = () => {
    setReloadNonce((nonce) => nonce + 1)
  }

  const tableItems = useMemo(
    () => getTransactionItems(transactionsDetails),
    [transactionsDetails],
  )
  const isLoadingMore = transactionsLoading && requestedPage > 1

  const sentinelRef = useInfiniteScroll({
    hasMore,
    isLoading: transactionsLoading,
    onLoadMore: () => requestPage(loadedPage + 1),
  })

  const handleChange = (
    event: ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const name = event.currentTarget.name
    const value = event.currentTarget.value

    if (name === 'id') {
      if (!value.length) {
        const newState = { ...filtersState }
        delete newState['id']

        setFiltersState(newState)
      } else {
        setFiltersState((prev) => ({
          ...prev,
          [name]: value,
        }))
      }
    } else if (name === 'type') {
      if (value === 'Choose type') {
        const newState = { ...filtersState }
        delete newState['type']

        setFiltersState(newState)
      } else {
        setFiltersState((prev) => ({
          ...prev,
          [name]: value,
        }))
      }
    }
  }

  const handleChangeCurrencies = (selected: Option[]) => {
    if (selected.length === 0) {
      const newState = { ...filtersState }
      delete newState['currency']

      setFiltersState(newState)
    } else {
      setFiltersState((prev) => ({
        ...prev,
        currency: selected.map((item) => item.value as string),
      }))
    }
  }

  const handleChangeTypes = (selected: Option[]) => {
    if (selected.length === 0) {
      const newState = { ...filtersState }
      delete newState['type']

      setFiltersState(newState)
    } else {
      setFiltersState((prev) => ({
        ...prev,
        type: selected.map((item) => item.value as string),
      }))
    }
  }

  const handleChangeWallets = (selected: Option[]) => {
    if (selected.length === 0) {
      const newState = { ...filtersState }
      delete newState['wallets']

      setFiltersState(newState)
    } else {
      setFiltersState((prev) => ({
        ...prev,
        wallets: selected.map((item) => item.value as string),
      }))
    }
  }

  const handelOpenEditModal = (
    modalName: keyof Omit<TransactionModalState, 'transactionId' | 'wallet'>,
    transaction: TransactionsDetailsItemFromServer,
  ) => {
    setIsOpenEditModal((prevState) => ({
      ...prevState,
      [modalName]: true,
      transaction,
    }))
  }

  const handelCloseEditModal = (
    modalName: keyof Omit<TransactionModalState, 'transactionId' | 'wallet'>,
  ) => {
    setIsOpenEditModal((prevState) => ({
      ...prevState,
      [modalName]: false,
      transactionId: '',
    }))
  }

  useEffect(() => {
    allCurrenciesMounted()
    loadWalletsWithArchive()
    walletAllowedListMounted()
  }, [])

  return (
    <Card style={{ width: '100%' }}>
      <Flex vertical gap={24}>
        <Flex justify="space-between">
          <Typography.Title level={3}>All transactions</Typography.Title>
          <div>
            <CButton
              color="primary"
              className="mb-2 me-2"
              size="sm"
              onClick={() => setIsModalOpen(true)}
            >
              + Add new transaction
            </CButton>
            <CButton
              color="primary"
              className="mb-2"
              onClick={openFilterModal}
              size="sm"
            >
              <CIcon className="me-1" icon={cilDataTransferDown} />
              Export to CSV
            </CButton>
          </div>
        </Flex>
        <div>
          <Flex justify="space-between">
            <Flex gap={5} style={{ height: 'auto', width: '80%' }}>
              <CFormInput
                style={{ width: '170px' }}
                placeholder="Search"
                id="id"
                name="id"
                size="sm"
                value={filtersState.id as string}
                onChange={handleChange}
              />
              <CMultiSelect
                style={{ width: '130px' }}
                virtualScroller
                placeholder="Type"
                size="sm"
                options={transactionDetailsType.map((item) => ({
                  text: item.label,
                  value: item.value,
                }))}
                onChange={handleChangeTypes}
              />
              <CMultiSelect
                style={{ width: '130px' }}
                virtualScroller
                placeholder="Wallet"
                size="sm"
                options={optionsWallet}
                onChange={handleChangeWallets}
              />
              {allCurrencies && (
                <CMultiSelect
                  className="w-50"
                  style={{ width: '335px' }}
                  virtualScroller
                  placeholder="Currency"
                  size="sm"
                  options={allCurrencies.map((item) => ({
                    text: item.code + ' (' + item.name + ')',
                    value: item.code,
                  }))}
                  onChange={handleChangeCurrencies}
                />
              )}
              <DatePicker.RangePicker
                format="YYYY-MM-DD"
                style={{ width: '270px' }}
                value={filtersState.period as any}
                onChange={(e) => {
                  const result = e ? [e] : []

                  setFiltersState((prevState) => ({
                    ...prevState,
                    period: result[0],
                  }))
                }}
                presets={transactionsRangePresets}
                allowEmpty={[true, true]}
              />
            </Flex>
          </Flex>
          <CSmartTable
            columnSorter={{ external: true }}
            columns={transactionTableColumns}
            tableProps={{
              striped: true,
              responsive: true,
            }}
            itemsPerPage={Math.max(tableItems.length, TRANSACTIONS_PAGE_SIZE)}
            pagination={false}
            items={tableItems}
            // @ts-ignore
            onSorterChange={(sorter) => {
              setColumnSorter(sorter)
            }}
            scopedColumns={mapScopedColumns(handelOpenEditModal)}
            loading={transactionsLoading && requestedPage === 1}
          />
          <div
            ref={sentinelRef}
            style={{ display: 'flex', justifyContent: 'center', padding: 16 }}
          >
            {isLoadingMore && <CSpinner size="sm" />}
          </div>
        </div>
      </Flex>
      {isModalOpen && (
        <GeneralTransactionModal
          isOpen={isModalOpen}
          currenciesFromWallet={currencies.map((item) => item.code)}
          allCurrencies={currencies}
          type="allTransactions"
          onClose={() => {
            setIsModalOpen(false)
            setTimeout(() => {
              reloadTransactions()
            }, 1000)
          }}
        />
      )}
      {isOpenEditModal.createOtherModal && (
        <OtherTransactionModal
          mode="edit"
          isOpen={isOpenEditModal.createOtherModal}
          transactionId={isOpenEditModal.transaction?.id}
          onClose={() => {
            handelCloseEditModal('createOtherModal')
            setTimeout(() => {
              reloadTransactions()
            }, 1000)
          }}
          type="allTransactions"
        />
      )}
      {isOpenEditModal.createTransferModal && (
        <TransferTransactionModal
          mode="edit"
          isOpen={isOpenEditModal.createTransferModal}
          transactionId={isOpenEditModal.transaction?.id}
          onClose={() => {
            handelCloseEditModal('createTransferModal')
            setTimeout(() => {
              reloadTransactions()
            }, 1000)
          }}
          type="allTransactions"
        />
      )}
      {isOpenEditModal.createDividendModal && (
        <DividendTransactionModal
          mode="edit"
          allCurrencies={allCurrencies}
          transactionId={isOpenEditModal.transaction?.id}
          transactionType={isOpenEditModal.transaction?.type}
          isOpen={isOpenEditModal.createDividendModal}
          onClose={() => {
            handelCloseEditModal('createDividendModal')
            setTimeout(() => {
              reloadTransactions()
            }, 1000)
          }}
          type="allTransactions"
        />
      )}
      {isOpenEditModal.createInvestmentModal && (
        <InvestmentTransactionModal
          mode="edit"
          allCurrencies={allCurrencies}
          transactionId={isOpenEditModal.transaction?.id}
          transactionType={isOpenEditModal.transaction?.type}
          isOpen={isOpenEditModal.createInvestmentModal}
          onClose={() => {
            handelCloseEditModal('createInvestmentModal')
            setTimeout(() => {
              reloadTransactions()
            }, 1000)
          }}
          type="allTransactions"
        />
      )}
      {isOpenEditModal.createBudgetPaymentModal && (
        <BudgetPaymentTransactionModal
          mode="edit"
          transactionId={isOpenEditModal.transaction?.id}
          isOpen={isOpenEditModal.createBudgetPaymentModal}
          onClose={() => {
            handelCloseEditModal('createBudgetPaymentModal')
            setTimeout(() => {
              reloadTransactions()
            }, 1000)
          }}
          type="allTransactions"
        />
      )}
      {isOpenEditModal.createCommissionPaymentModal && (
        <CommissionPaymentTransactionModal
          mode="edit"
          transactionId={isOpenEditModal.transaction?.id}
          isOpen={isOpenEditModal.createCommissionPaymentModal}
          onClose={() => {
            handelCloseEditModal('createCommissionPaymentModal')
            reloadTransactions()
          }}
          type="allTransactions"
        />
      )}
      <FilterModal
        isOpen={isFilterModalVisible}
        allCurrencies={allCurrencies}
        onClose={() => setIsFilterModalVisible(false)}
      />
    </Card>
  )
}
